/**
 * Content validation utilities for checking text against banned words
 */

export interface ValidationResult {
    isValid: boolean
    error?: string
}

/**
 * Tokenizes text into words, handling punctuation and special characters
 * @param text The text to tokenize
 * @returns Array of lowercase words
 */
function tokenizeText(text: string): string[] {
    // Split by whitespace and punctuation, remove empty strings
    // Match word boundaries to handle contractions properly
    const tokens = text.toLowerCase().match(/\b[\w']+\b/g) || []
    return tokens
}

/**
 * Checks if text contains any banned words (full word match, case-insensitive)
 * @param text The text to validate
 * @param bannedWords Array of banned words/phrases (already lowercase)
 * @returns ValidationResult with isValid flag and optional error message
 */
export function validateContent(text: string, bannedWords: string[]): ValidationResult {
    if (!text || !text.trim()) {
        return { isValid: true }
    }

    if (!bannedWords || bannedWords.length === 0) {
        return { isValid: true }
    }

    const normalizedText = text.toLowerCase()
    const tokens = tokenizeText(text)

    // Check for multi-word banned phrases first
    for (const bannedWord of bannedWords) {
        if (bannedWord.includes(" ")) {
            // Multi-word phrase - check if it exists in the text
            const phraseRegex = new RegExp(`\\b${bannedWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i")
            if (phraseRegex.test(normalizedText)) {
                return {
                    isValid: false,
                    error: "Your message contains content that violates our guidelines",
                }
            }
        } else {
            // Single word - check if it exists as a complete token
            if (tokens.includes(bannedWord)) {
                return {
                    isValid: false,
                    error: "Your message contains content that violates our guidelines",
                }
            }
        }
    }

    return { isValid: true }
}
